import { memo, useEffect, useState } from 'react';
import { currentUserId$ } from '../../../functions/auth/utils';
import {
  database,
  databaseServerNow,
} from '../../../functions/firebase/firebaseAccess';
import { useIsAppActive } from '../../../functions/hooks/useAppState';
import { useObservable } from '../../../functions/hooks/useObservable';
import { useDeviceId } from '../../../functions/user/sessionTracker';

enum OnlineState {
  online = 'online',
  offline = 'offline',
}

type ConnectionState = {
  state: OnlineState;
  first_connected_at?: any;
  last_connected_at?: any;
  disconnected_at?: any;
  left_at?: any;
};

const PING_INTERVAL = 60 * 1000;

export const OnLineStatusTracking = memo(() => {
  const deviceId = useDeviceId();
  const uid = useObservable(
    currentUserId$,
    'OnLineStatusTracking-current-user-id',
  );

  const isAppActive = useIsAppActive();
  const [isRealtimeDatabaseConnected, setIsRealtimeDatabaseConnected] =
    useState(false);
  useEffect(() => {
    database()
      .ref('.info/connected')
      .on('value', snapshot => setIsRealtimeDatabaseConnected(snapshot.val()));
    return () => {
      database().ref('.info/connected').off('value');
    };
  }, []);
  useEffect(() => {
    // https://firebase.google.com/docs/reference/js/firebase.database.OnDisconnect
    // If we're not currently connected, don't do anything.
    if (uid == null || !isAppActive || !isRealtimeDatabaseConnected) {
      return;
    }

    const connection = database()
      .ref(`/status/${uid}/devices/${deviceId}`)
      .push();
    const connectionDisconnect = connection.onDisconnect();

    const pingIntervalRef = setInterval(() => {
      connection
        .update({
          last_connected_at: databaseServerNow,
        })
        .catch(() => null);
    }, PING_INTERVAL);
    connectionDisconnect
      .update({
        state: OnlineState.offline,
        disconnected_at: databaseServerNow,
      } as ConnectionState)
      .then(
        () => {
          // The promise returned from .onDisconnect().set() will
          // resolve as soon as the server acknowledges the onDisconnect()
          // request, NOT once we've actually disconnected:

          // We can now safely set ourselves as 'online' knowing that the
          // server will mark us as offline once we lose connection.
          connection
            .set({
              first_connected_at: databaseServerNow,
              last_connected_at: databaseServerNow,
              state: OnlineState.online,
            } as ConnectionState)
            .catch(() => {
              connectionDisconnect.cancel().catch(() => null);
            });
        },
        () => null,
      );

    return () => {
      clearInterval(pingIntervalRef);
      connection
        .update({
          left_at: databaseServerNow,
          state: OnlineState.offline,
        } as ConnectionState)
        .then(
          function () {
            connectionDisconnect.cancel().catch(() => null);
          },
          () => null,
        );
    };
  }, [uid, deviceId, isAppActive, isRealtimeDatabaseConnected]);

  return null;
});
